import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as apiLogin, register as apiRegister, signInWithGoogle as apiSignInWithGoogle, resetPasswordRequest as apiResetPasswordRequest, verifyEmail as apiVerifyEmail, logout as apiLogout, type AuthResult } from '../apis/auth';
import { UserProfile } from '../types';
import { fetchUserProfileApi } from '../apis/user';
import { useSnackbar } from "./SnackbarContext";
import { useUserStore } from '../store/user';
import { useCartStore } from '../store/cart';
import MainLoader from '../components/shared/MainLoader';

interface AuthContextProps {
  currentUser: UserProfile | null;
  loading: boolean;
  register: (
    email: string,
    password: string,
    fullname: string,
    agreeToTerms: boolean,
  ) => Promise<AuthResult>;
  login: (email: string, password: string) => Promise<AuthResult>;
  /**
   * Resolves with `isNewUser` so the caller can tell a Google *registration*
   * from a Google *sign-in* — one endpoint serves both, and they are different
   * conversions.
   */
  signInWithGoogle: (idToken: string) => Promise<AuthResult>;
  resetPasswordRequest: (email: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { showSnackbar } = useSnackbar();
  const { initializeUser, resetStore } = useUserStore();

  useEffect(() => {
    const loadUserFromToken = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
        
          const decodedToken: any = JSON.parse(atob(token.split('.')[1])); // Basic JWT decode
          const userProfile = await fetchUserProfileApi(decodedToken.id); // Assuming 'id' in token payload
          if (userProfile) {
            setCurrentUser(userProfile);
            initializeUser(userProfile);
          } else {
            localStorage.removeItem('token');
            setCurrentUser(null);
            resetStore();
          }
        } catch (error) {
          console.error("Error loading user from token:", error);
          localStorage.removeItem('token');
          setCurrentUser(null);
          resetStore();
        }
      }
      setLoading(false);
    };

    loadUserFromToken();
  }, [initializeUser, resetStore]);

  const login = async (email: string, password: string) => {
    console.log('[AUTH] Login attempt started');
    setLoading(true);
    try {
      const { token, user } = await apiLogin(email, password);
      console.log('[AUTH] Login API success');
      localStorage.setItem('token', token);
      setCurrentUser(user);
      initializeUser(user);
      console.log('[AUTH] Login completed successfully');
      return { token, user };
    } finally {
      setLoading(false);
      console.log('[AUTH] Login finally block');
    }
  };

  /**
   * `agreeToTerms` is threaded through rather than defaulted.
   *
   * The server records the acceptance against a terms version, so what it
   * stores has to be what the person actually did. A default here would make
   * the API's requirement satisfiable without anyone ticking anything, which is
   * the same gap that made the checkbox decorative in the first place.
   */
  const register = async (
    email: string,
    password: string,
    fullname: string,
    agreeToTerms: boolean,
  ) => {
    setLoading(true);
    try {
      const { token, user } = await apiRegister(email, password, fullname, agreeToTerms);
      localStorage.setItem('token', token);
      setCurrentUser(user);
      initializeUser(user);
      return { token, user };
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async (idToken: string) => {
    setLoading(true);
    try {
      const { token, user, isNewUser } = await apiSignInWithGoogle(idToken);
      localStorage.setItem('token', token);
      setCurrentUser(user);
      initializeUser(user);
      return { token, user, isNewUser };
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordRequest = async (email: string) => {
    await apiResetPasswordRequest(email);
  };

  const verifyEmail = async (token: string) => {
    try {
      await apiVerifyEmail(token);
      showSnackbar('Email verified successfully!', 'success');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Email verification failed';
      showSnackbar(errorMessage, 'error');
      return Promise.reject(new Error(errorMessage));
    }
  };

  /**
   * Everything that belonged to the session goes, including the basket.
   *
   * `mindalitix_cart` is a plain localStorage key with no user in it, and
   * neither this nor `resetStore` used to touch it — so on a shared device the
   * next person to sign in inherited the previous one's basket, priced items and
   * all, and could take it to checkout. A cart is per-person state; it ends when
   * the session does.
   */
  const clearSession = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
    resetStore();
    useCartStore.getState().clearCart();
  };

  const logout = async () => {
    setLoading(true);
    try {
      await apiLogout(); // Call backend logout if needed for token invalidation
      clearSession();
      showSnackbar('Logged out successfully!', 'success');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Logout failed';
      showSnackbar(errorMessage, 'error');
      // Even if logout fails, clear local state
      clearSession();
      return Promise.reject(new Error(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    register,
    login,
    signInWithGoogle,
    resetPasswordRequest,
    verifyEmail,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <MainLoader /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};